import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastComponent: React.FC<ToastProps> = ({ toast, onRemove }) => {
  useEffect(() => {
    const ms = toast.duration ?? 5000;
    if (ms <= 0) {
      return;
    }
    const timer = setTimeout(() => {
      onRemove(toast.id);
    }, ms);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onRemove]);

  const iconMap = {
    success: <CheckCircle className="w-5 h-5" />,
    error: <XCircle className="w-5 h-5" />,
    warning: <AlertCircle className="w-5 h-5" />,
    info: <Info className="w-5 h-5" />,
  };

  const colorMap = {
    success: {
      bg: 'bg-success-50',
      border: 'border-success-500',
      text: 'text-success-800',
      icon: 'text-success-500',
    },
    error: {
      bg: 'bg-danger-50',
      border: 'border-danger-500',
      text: 'text-danger-800',
      icon: 'text-danger-500',
    },
    warning: {
      bg: 'bg-warning-50',
      border: 'border-warning-500',
      text: 'text-warning-800',
      icon: 'text-warning-500',
    },
    info: {
      bg: 'bg-primary-50',
      border: 'border-primary-500',
      text: 'text-primary-800',
      icon: 'text-primary-500',
    },
  };

  const colors = colorMap[toast.type];

  return (
    <div
      className={`w-full max-w-md min-w-0 shrink-0 p-4 rounded-lg shadow-large flex items-start gap-3 animate-slide-in-right border-l-4 ${colors.bg} ${colors.border}`}
      role="alert"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
    >
      <div className={`flex-shrink-0 ${colors.icon}`}>
        {iconMap[toast.type]}
      </div>
      <p
        className={`flex-1 min-w-0 text-sm font-medium max-h-48 overflow-y-auto whitespace-pre-wrap break-words ${colors.text}`}
      >
        {toast.message}
      </p>
      <button
        type="button"
        onClick={() => onRemove(toast.id)}
        className={`flex-shrink-0 inline-flex items-center justify-center min-w-[44px] min-h-[44px] -m-2 rounded-md ${colors.text} hover:opacity-70 transition-opacity`}
        aria-label="Close notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ToastComponent;

